/**
 * Reading a drawing as a worker job (design 2026-09-23 §3.5): queue it for
 * stored documents, poll its status every 1,5 s while it runs, and take the
 * schema view out of the answer once it is read.
 */
import { useQuery } from "@tanstack/react-query";

import { ProblemError, toProblemDetails, type components } from "@k60/contracts";

import { api } from "./client";
import type { DemoRunResult } from "./demo";

export type ExtractionOut = components["schemas"]["ExtractionOut"];
export type ExtractionEnqueuedOut = components["schemas"]["ExtractionEnqueuedOut"];
export type ExtractionStatus = ExtractionOut["status"];
export type ExtractionStage = NonNullable<ExtractionOut["stage"]>;
/** Seitenwahl (13.10.2026): the one page of one document a reading reads. */
export type PageChoice = components["schemas"]["PageChoiceIn"];

export const EXTRACTION_POLL_MS = 1500;
export const NO_EXTRACTION = "Zu diesem Vorgang gibt es keine Auslese.";

export function extractionKey(caseId: string) {
  return ["case", caseId, "extraction"] as const;
}

/** Poll while the worker still has something to say; stop once it has. */
export function pollingInterval(status: ExtractionStatus | undefined): number | false {
  return status === "queued" || status === "running" ? EXTRACTION_POLL_MS : false;
}

/**
 * Queue a reading of stored documents. With `page` (Seitenwahl, 13.10.2026)
 * only that page is read: the server stores it as a sheet of its own and
 * reads the sheet; `documentIds` must then name exactly its document (422
 * otherwise, with a German `detail`). Without `page` the body is what it was.
 */
export async function startExtraction(
  caseId: string,
  documentIds: string[],
  page?: PageChoice,
): Promise<ExtractionEnqueuedOut> {
  const { data, response } = await api.POST("/api/v1/cases/{case_id}/extraction", {
    params: { path: { case_id: caseId } },
    body: page ? { documentIds, page } : { documentIds },
  });
  if (!data) throw new ProblemError(toProblemDetails(null, response.status), null);
  return data;
}

export async function retryExtraction(caseId: string): Promise<ExtractionEnqueuedOut> {
  const { data, response } = await api.POST("/api/v1/cases/{case_id}/extraction/retry", {
    params: { path: { case_id: caseId } },
  });
  if (!data) throw new ProblemError(toProblemDetails(null, response.status), null);
  return data;
}

/**
 * Ask again after a failure — once, like every query of the app — but not
 * after a 404. „There is no reading“ is an answer, not a failure: asked
 * again it says the same. And a retry is the one fetch React Query holds
 * back while the tab is hidden — a 404 retried in a background tab kept
 * `/einlesen` on „Die Lesung wird geladen …“ until somebody looked
 * (Kauf-Test 11.10.2026).
 */
export function shouldRetryExtraction(failureCount: number, error: Error): boolean {
  return error.message !== NO_EXTRACTION && failureCount < 1;
}

/** How closely one screen watches the reading. */
export interface ExtractionWatch {
  /**
   * Keep polling while the tab is hidden, and ask at once when it is seen
   * again. For the one screen that waits for the reading (`/einlesen`): it
   * says in the tab's title that the drawing is read, and it can only say so
   * if it hears it. A few small GETs for at most a couple of minutes; every
   * other screen stops polling in the background as React Query does.
   */
  inBackground?: boolean;
}

/**
 * The reading of one Vorgang, polled while the worker still has it.
 *
 * A 404 (no reading exists yet, or none for this case) becomes the same
 * `NO_EXTRACTION` message whichever shape it arrives in — the stubbed
 * `{ data, response }` a test hands back without throwing, and the real
 * client's thrown `ProblemError` (`api/client.ts`'s shared error middleware).
 */
export function useExtraction(caseId: string | null, watch: ExtractionWatch = {}) {
  return useQuery({
    queryKey: extractionKey(caseId ?? ""),
    enabled: caseId !== null,
    queryFn: async (): Promise<ExtractionOut> => {
      try {
        const { data, response } = await api.GET("/api/v1/cases/{case_id}/extraction", {
          params: { path: { case_id: caseId! } },
        });
        if (response.status === 404) throw new Error(NO_EXTRACTION);
        if (!data) throw new ProblemError(toProblemDetails(null, response.status), null);
        return data;
      } catch (cause) {
        if (cause instanceof ProblemError && cause.status === 404) throw new Error(NO_EXTRACTION);
        throw cause;
      }
    },
    retry: shouldRetryExtraction,
    refetchInterval: (query) => pollingInterval(query.state.data?.status),
    // Only set where asked: an option passed as `undefined` would not fall
    // back to the app's `refetchOnWindowFocus: false` — React Query reads it
    // as „not false“, so as true.
    ...(watch.inBackground ? { refetchIntervalInBackground: true, refetchOnWindowFocus: true } : {}),
  });
}

/** The schema view inside a status answer, in the shape every schema screen reads. */
export function extractionResult(out: ExtractionOut): DemoRunResult | null {
  return (out.result ?? null) as unknown as DemoRunResult | null;
}

/** The page the model actually saw, turned — not the original file (Task 6 review). */
export function extractionPageUrl(caseId: string, page: number): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? "";
  return `${base}/api/v1/cases/${encodeURIComponent(caseId)}/extraction/pages/${page}`;
}
